/**
 * @bos/portal3 — THE LATER-STAGE EXACT DOOR (register A2, the six-stage half).
 *
 * The stage reviews have had ONE door since the A3/A4 batch: say what is wrong in your own words, and the
 * machine derives that stage again around them. That is a REQUEST — paid, and it can come back unchanged.
 * This is the other door, where the stage's bytes honestly carry one: an ORDER. A typed change the chain
 * applies mechanically at that stage's boundary, costing no derivation and guaranteed to land.
 *
 * ── WHAT IS OFFERED IS READ OFF THE SCREEN'S OWN BYTES ───────────────────────────────────────────────
 * `stageEditOffers` builds the composer's choices from the SAME shaped doc the review renders — the states
 * a lifecycle actually has, the operations that actually exist, the permissions some operation actually
 * needs. Nothing is typed free-hand except a NEW name (a new state, a new event), because those are the
 * only two values that cannot come from something already on the screen. There is no field for a value the
 * model could not hold.
 *
 * ── THE FIVE AND THE ONE ─────────────────────────────────────────────────────────────────────────────
 * Lifecycles, operations, rules, permissions and record-keeping each get a vocabulary. The MODULES review
 * gets none, and {@link ProseOnlyNote} says so plainly instead of showing a door that could not land. That
 * is not a gap: nothing downstream reads the module summary as a hash, the packs the build is made from
 * are minted inside the same stage, and the module cut already has its own ratify ceremony.
 *
 * ── THE BASKET IS THE DATA MODEL'S ───────────────────────────────────────────────────────────────────
 * One list, order-significant, previewed by chaining: change k is shown against the model as changes
 * 1..k-1 left it. Nothing is committed until the basket is applied, and a single refusal declines the
 * whole basket — a person composed the list as a unit.
 */
import { useEffect, useRef, useState } from 'react';
import type {
  ApplyStageBasketResult,
  AuditDoc,
  LifecyclesDoc,
  OperationsDoc,
  OrderStageEditResult,
  PermissionsDoc,
  RulesDoc,
  RunStageBasketPreview,
  RunStageEditPreview,
  StageEditPatch,
} from './hooks';
import type { LifecycleMachineDoc, RulePreconditionDoc } from '@bos/portal3-server';
import { runErrorMessage, type StageReviewPayload } from './hooks';
import { STAGE_SUBJECTS, type StageReviewKey } from './model';

// ── the offer model ──────────────────────────────────────────────────────────

/** One value a composer field can take, with the words the person reads. */
export interface EditChoice {
  readonly value: string;
  readonly label: string;
}

/** One input the composer shows. `select` choices always come from the bytes on screen. */
export type EditField =
  | { readonly key: string; readonly label: string; readonly kind: 'select'; readonly choices: readonly EditChoice[] }
  | { readonly key: string; readonly label: string; readonly kind: 'text'; readonly hint?: string };

/** One thing a person can order at this stage. `fields` is a function of the values chosen so far, so a
 *  second dropdown lists only what belongs to the first one's answer. */
export interface EditOffer {
  readonly kind: StageEditPatch['kind'];
  readonly label: string;
  readonly fields: (v: Readonly<Record<string, string>>) => readonly EditField[];
  /** The composed change, or null while an input it needs is still empty. */
  readonly build: (v: Readonly<Record<string, string>>) => StageEditPatch | null;
}

/** Values that name several members at once ride one option, joined by a character no model name can
 *  contain (a state and an event are both letters/digits/underscore), so the split back is always exact. */
const SEP = '>';
/** The same joiner, exported so a gesture on the lifecycle picture can compose exactly the value
 *  the "Which move?" dropdown carries — one encoding, never two. */
export const MOVE_SEP = SEP;
const nonEmpty = (s: string | undefined): string | null => (s !== undefined && s.trim() !== '' ? s.trim() : null);

/** A precondition as a one-line choice label — the review's own sentence, shortened where it runs long. */
function preconditionChoice(pc: RulePreconditionDoc): string {
  const parts = [pc.entity, pc.field, pc.idFrom].filter((x): x is string => x !== null);
  const head = pc.kind === 'custom' ? (pc.description ?? 'a written requirement') : `${pc.kind}${parts.length > 0 ? `: ${parts.join('.')}` : ''}`;
  const tail = pc.allowed.length > 0 ? ` (${pc.allowed.join(', ')})` : pc.min !== null ? ` (at least ${pc.min})` : '';
  const full = head + tail;
  return full.length > 110 ? `${full.slice(0, 107)}…` : full;
}

// ── the five vocabularies, each built from the screen's own bytes ────────────

/**
 * The states a machine will have once the BASKET so far has landed — the derived states plus what earlier
 * members add, minus what they remove, through any rename they make.
 *
 * WHY THE BASKET AND NOT JUST DISK. A basket exists to compose dependent changes: add a state, then a move
 * into it. Offering only the states already on disk would make the second change uncomposable — the person
 * would have to apply, wait, and come back, which is the sequential door with extra steps. A pending state
 * is LABELLED as pending, so nobody mistakes it for something the machine derived, and the server's chained
 * dry run remains the authority: if the composition is wrong, the preview refuses and says why.
 */
function statesAfterBasket(base: readonly string[], entity: string, basket: readonly StageEditPatch[]): Array<{ name: string; pending: boolean }> {
  const out = base.map((name) => ({ name, pending: false }));
  for (const p of basket) {
    if (!('entity' in p) || p.entity !== entity) continue;
    if (p.kind === 'sm-state-add') {
      if (!out.some((s) => s.name === p.state)) out.push({ name: p.state, pending: true });
    } else if (p.kind === 'sm-state-rename') {
      // The NAME is the basket's, not the machine's — the label must say so (C2: a renamed
      // state offered bare would read as something the machine derived).
      for (const s of out) {
        if (s.name === p.from) {
          s.name = p.to;
          s.pending = true;
        }
      }
    } else if (p.kind === 'sm-state-remove') {
      const i = out.findIndex((s) => s.name === p.state);
      if (i >= 0) out.splice(i, 1);
    }
  }
  return out;
}

/** One state of a machine as the picture must draw it: derived, or only in the basket. */
export interface BasketState {
  readonly name: string;
  readonly pending: boolean;
}

/** One move of a machine as the picture must draw it. A move the BASKET adds carries no guard
 *  and no rule, because nobody has derived one for it — a blank is the honest answer. */
export interface BasketMove {
  readonly from: string;
  readonly to: string;
  readonly event: string | null;
  readonly guard: string | null;
  readonly enforcesRule: string | null;
  readonly pending: boolean;
}

export interface MachineAfterBasket {
  readonly states: readonly BasketState[];
  readonly initial: string | null;
  readonly terminal: readonly string[];
  readonly transitions: readonly BasketMove[];
}

/**
 * THE MACHINE THE PICTURE DRAWS while a basket is composed: the DERIVED machine, plus the
 * basket's ADDITIONS marked pending — and nothing else.
 *
 * WHY ADDITIONS AND ONLY ADDITIONS. A basket exists to compose dependent changes: add a state,
 * then a move into it. If the diagram drew only what is on disk, the second gesture would have
 * nothing to drag onto — so a pending state and a pending move ARE drawn, each marked (a dashed
 * brass pill and the words "in the basket"), so nothing is ever mistaken for something the
 * machine derived. The other four kinds have no such mark to wear: a REMOVAL drawn is an
 * absence, a RENAME drawn is the new name over the old pill, a TERMINAL flip is the ring
 * itself (aria: "the end of the road") — drawing any of them before the basket applies would
 * present an ordered-but-unapplied change as already true, which is the one lie this product
 * does not tell. So those stay in the basket panel below, and the picture keeps the disk
 * truth until they actually land. The composer's dropdowns ({@link statesAfterBasket}) still
 * fold everything, labelled, so dependent edits remain composable by hand — and the server's
 * chained dry run remains the only authority on whether a composition is legal.
 */
export function machineAfterBasket(
  machine: LifecycleMachineDoc,
  basket: readonly StageEditPatch[],
): MachineAfterBasket {
  const states: Array<{ name: string; pending: boolean }> = machine.states.map((name) => ({ name, pending: false }));
  const transitions: Array<{
    from: string;
    to: string;
    event: string | null;
    guard: string | null;
    enforcesRule: string | null;
    pending: boolean;
  }> = machine.transitions.map((t) => ({
    from: t.from,
    to: t.to,
    event: t.event,
    guard: t.guard,
    enforcesRule: t.enforcesRule,
    pending: false,
  }));

  for (const p of basket) {
    if (!('entity' in p) || p.entity !== machine.entity) continue;
    if (p.kind === 'sm-state-add') {
      if (!states.some((s) => s.name === p.state)) states.push({ name: p.state, pending: true });
    } else if (p.kind === 'sm-transition-add') {
      if (!transitions.some((t) => t.from === p.from && t.to === p.to && t.event === p.event)) {
        transitions.push({ from: p.from, to: p.to, event: p.event, guard: null, enforcesRule: null, pending: true });
      }
    }
    // sm-state-rename / sm-state-remove / sm-terminal-set / sm-transition-remove: NOT drawn.
    // See the doc above — an unapplied change the picture cannot mark is a change the
    // picture must not paint as already true.
  }

  return { states, initial: machine.initial, terminal: machine.terminal, transitions };
}

function lifecycleOffers(doc: LifecyclesDoc, basket: readonly StageEditPatch[]): EditOffer[] {
  const machines = doc.machines;
  const entityChoices = machines.map((m) => ({ value: m.entity, label: m.entity }));
  const machineOf = (v: Readonly<Record<string, string>>) => machines.find((m) => m.entity === v.entity);
  const statesOf = (v: Readonly<Record<string, string>>): EditChoice[] =>
    statesAfterBasket(machineOf(v)?.states ?? [], v.entity ?? '', basket).map((s) => ({
      value: s.name,
      label: s.pending ? `${s.name} (in the basket)` : s.name,
    }));
  const entityField: EditField = { key: 'entity', label: 'Which thing?', kind: 'select', choices: entityChoices };

  return [
    {
      kind: 'sm-state-add',
      label: 'Add a state it can be in',
      fields: () => [entityField, { key: 'state', label: 'New state', kind: 'text', hint: 'e.g. OnHold' }],
      build: (v) => {
        const state = nonEmpty(v.state);
        return v.entity !== undefined && state !== null ? { kind: 'sm-state-add', entity: v.entity, state } : null;
      },
    },
    {
      kind: 'sm-state-rename',
      label: 'Call a state something else',
      fields: (v) => [
        entityField,
        { key: 'from', label: 'Which state?', kind: 'select', choices: statesOf(v) },
        { key: 'to', label: 'New name', kind: 'text' },
      ],
      build: (v) => {
        const to = nonEmpty(v.to);
        return v.entity !== undefined && v.from !== undefined && to !== null
          ? { kind: 'sm-state-rename', entity: v.entity, from: v.from, to }
          : null;
      },
    },
    {
      kind: 'sm-state-remove',
      label: 'Remove a state',
      fields: (v) => [entityField, { key: 'state', label: 'Which state?', kind: 'select', choices: statesOf(v) }],
      build: (v) =>
        v.entity !== undefined && v.state !== undefined ? { kind: 'sm-state-remove', entity: v.entity, state: v.state } : null,
    },
    {
      kind: 'sm-transition-add',
      label: 'Allow a new move between states',
      fields: (v) => [
        entityField,
        { key: 'from', label: 'From', kind: 'select', choices: statesOf(v) },
        { key: 'to', label: 'To', kind: 'select', choices: statesOf(v) },
        { key: 'event', label: 'What is this called?', kind: 'text', hint: 'e.g. hold' },
      ],
      build: (v) => {
        const event = nonEmpty(v.event);
        return v.entity !== undefined && v.from !== undefined && v.to !== undefined && event !== null
          ? { kind: 'sm-transition-add', entity: v.entity, from: v.from, to: v.to, event }
          : null;
      },
    },
    {
      kind: 'sm-transition-remove',
      label: 'Stop a move being allowed',
      fields: (v) => [
        entityField,
        {
          key: 'move',
          label: 'Which move?',
          kind: 'select',
          // A move with no recorded event cannot be addressed exactly (the change names all three), so it
          // is not offered — better absent than a control that always refuses.
          choices: [
            ...(machineOf(v)?.transitions ?? [])
              .filter((t) => t.event !== null)
              .map((t) => ({ value: [t.from, t.to, t.event ?? ''].join(SEP), label: `${t.from} → ${t.to} on ${t.event ?? ''}` })),
            // …and the moves an earlier basket member adds, labelled as pending (see statesAfterBasket)
            ...basket
              .filter((p): p is Extract<StageEditPatch, { kind: 'sm-transition-add' }> => p.kind === 'sm-transition-add' && p.entity === v.entity)
              .map((p) => ({ value: [p.from, p.to, p.event].join(SEP), label: `${p.from} → ${p.to} on ${p.event} (in the basket)` })),
          ],
        },
      ],
      build: (v) => {
        if (v.entity === undefined || v.move === undefined) return null;
        const [from, to, event] = v.move.split(SEP);
        return from !== undefined && to !== undefined && event !== undefined && event !== ''
          ? { kind: 'sm-transition-remove', entity: v.entity, from, to, event }
          : null;
      },
    },
    {
      kind: 'sm-terminal-set',
      label: 'Change whether a state is the end of the road',
      fields: (v) => [
        entityField,
        { key: 'state', label: 'Which state?', kind: 'select', choices: statesOf(v) },
        {
          key: 'terminal',
          label: 'Is it final?',
          kind: 'select',
          choices: [
            { value: 'yes', label: 'Yes — nothing happens after it' },
            { value: 'no', label: 'No — it can still move on' },
          ],
        },
      ],
      build: (v) =>
        v.entity !== undefined && v.state !== undefined && v.terminal !== undefined
          ? { kind: 'sm-terminal-set', entity: v.entity, state: v.state, terminal: v.terminal === 'yes' }
          : null,
    },
  ];
}

function operationOffers(doc: OperationsDoc): EditOffer[] {
  return [
    {
      kind: 'pr-operation-remove',
      label: 'Remove something the app does',
      fields: () => [
        {
          key: 'operation',
          label: 'Which one?',
          kind: 'select',
          choices: doc.operations.map((o) => ({ value: o.name, label: o.name })),
        },
      ],
      build: (v) => (v.operation !== undefined ? { kind: 'pr-operation-remove', operation: v.operation } : null),
    },
  ];
}

function rulesOffers(doc: RulesDoc): EditOffer[] {
  const opChoices = doc.operations.map((o) => ({ value: o.operation, label: o.operation }));
  const rowOf = (v: Readonly<Record<string, string>>) => doc.operations.find((o) => o.operation === v.operation);
  const offers: EditOffer[] = [
    {
      kind: 'ru-precondition-remove',
      label: 'Stop requiring something before an action',
      fields: (v) => [
        { key: 'operation', label: 'On which action?', kind: 'select', choices: opChoices },
        {
          key: 'row',
          label: 'Which requirement?',
          kind: 'select',
          choices: (rowOf(v)?.preconditions ?? []).map((pc) => ({ value: pc.row, label: preconditionChoice(pc) })),
        },
      ],
      build: (v) =>
        v.operation !== undefined && v.row !== undefined
          ? { kind: 'ru-precondition-remove', operation: v.operation, row: v.row }
          : null,
    },
    {
      kind: 'ru-errorcase-remove',
      label: 'Stop reporting an error',
      fields: (v) => [
        { key: 'operation', label: 'On which action?', kind: 'select', choices: opChoices },
        {
          key: 'row',
          label: 'Which error?',
          kind: 'select',
          choices: (rowOf(v)?.errorCases ?? []).map((ec) => ({
            value: ec.row,
            label: ec.message ?? ec.when ?? ec.code ?? 'an error',
          })),
        },
      ],
      build: (v) =>
        v.operation !== undefined && v.row !== undefined ? { kind: 'ru-errorcase-remove', operation: v.operation, row: v.row } : null,
    },
  ];
  // The always-true rules are offered only when the model has some — three of the four archived runs do,
  // one does not, and an empty dropdown is a worse answer than no control.
  if (doc.invariants.length > 0) {
    offers.push({
      kind: 'ru-invariant-remove',
      label: 'Remove a rule that always has to hold',
      fields: () => [
        {
          key: 'row',
          label: 'Which rule?',
          kind: 'select',
          choices: doc.invariants.map((inv) => ({ value: inv.row, label: inv.name ?? inv.text ?? 'a rule' })),
        },
      ],
      build: (v) => (v.row !== undefined ? { kind: 'ru-invariant-remove', row: v.row } : null),
    });
  }
  return offers;
}

/**
 * THE CLOSED SET OF PERMISSIONS, sorted. A permission may be granted only when some operation
 * actually asks for it — the same closed set the engine refuses outside of, read off the
 * operations grid the screen is already showing. Shared by the composer's dropdowns and the
 * matrix's rows, so the two can never offer different things.
 */
export function permissionsInUse(doc: PermissionsDoc): string[] {
  const inUse = new Set<string>();
  for (const o of doc.operations) {
    if (o.permission !== null) inUse.add(o.permission);
    for (const p of o.authPermissions) inUse.add(p);
  }
  return [...inUse].sort();
}

/** What one role holds today, straight off the grant matrix. The matrix's ✓ and the composer's
 *  "stop them…" list are the same fold, so a cell can never disagree with the dropdown. */
export function permissionsHeldBy(doc: PermissionsDoc, role: string): readonly string[] {
  return doc.grants.find((g) => g.role === role)?.permissions ?? [];
}

function permissionOffers(doc: PermissionsDoc): EditOffer[] {
  const roleChoices = doc.roles.map((r) => ({ value: r.id, label: r.id }));
  const inUse = permissionsInUse(doc);
  const heldBy = (role: string): readonly string[] => permissionsHeldBy(doc, role);
  return [
    {
      kind: 'rb-grant-add',
      label: 'Let a role do something more',
      fields: (v) => [
        { key: 'role', label: 'Which role?', kind: 'select', choices: roleChoices },
        {
          key: 'permission',
          label: 'Let them…',
          kind: 'select',
          choices: [...inUse]
            .filter((p) => v.role === undefined || !heldBy(v.role).includes(p))
            .sort()
            .map((p) => ({ value: p, label: p })),
        },
      ],
      build: (v) =>
        v.role !== undefined && v.permission !== undefined ? { kind: 'rb-grant-add', role: v.role, permission: v.permission } : null,
    },
    {
      kind: 'rb-grant-remove',
      label: 'Stop a role doing something',
      fields: (v) => [
        { key: 'role', label: 'Which role?', kind: 'select', choices: roleChoices },
        {
          key: 'permission',
          label: 'Stop them…',
          kind: 'select',
          choices: (v.role !== undefined ? heldBy(v.role) : []).map((p) => ({ value: p, label: p })),
        },
      ],
      build: (v) =>
        v.role !== undefined && v.permission !== undefined
          ? { kind: 'rb-grant-remove', role: v.role, permission: v.permission }
          : null,
    },
    {
      kind: 'rb-role-remove',
      label: 'Remove a role',
      fields: () => [{ key: 'role', label: 'Which role?', kind: 'select', choices: roleChoices }],
      build: (v) => (v.role !== undefined ? { kind: 'rb-role-remove', role: v.role } : null),
    },
  ];
}

function auditOffers(doc: AuditDoc): EditOffer[] {
  const opChoices = doc.operations.map((o) => ({ value: o.operation, label: o.operation }));
  const entriesOf = (v: Readonly<Record<string, string>>) =>
    doc.operations.find((o) => o.operation === v.operation)?.entries ?? [];
  const entryField = (v: Readonly<Record<string, string>>): EditField => ({
    key: 'action',
    label: 'Which record?',
    kind: 'select',
    choices: entriesOf(v).map((e) => ({ value: e.action, label: e.action })),
  });
  return [
    {
      kind: 'au-entry-remove',
      label: 'Stop recording something',
      fields: (v) => [{ key: 'operation', label: 'On which action?', kind: 'select', choices: opChoices }, entryField(v)],
      build: (v) =>
        v.operation !== undefined && v.action !== undefined ? { kind: 'au-entry-remove', operation: v.operation, action: v.action } : null,
    },
    {
      kind: 'au-detail-field-remove',
      label: 'Stop recording one detail',
      fields: (v) => [
        { key: 'operation', label: 'On which action?', kind: 'select', choices: opChoices },
        entryField(v),
        {
          key: 'field',
          label: 'Which detail?',
          kind: 'select',
          choices: (entriesOf(v).find((e) => e.action === v.action)?.fields ?? []).map((f) => ({ value: f, label: f })),
        },
      ],
      build: (v) =>
        v.operation !== undefined && v.action !== undefined && v.field !== undefined
          ? { kind: 'au-detail-field-remove', operation: v.operation, action: v.action, field: v.field }
          : null,
    },
  ];
}

/**
 * What this stage can be ORDERED to do, given what it actually derived — plus, for the lifecycles, what the
 * BASKET so far has already added (see statesAfterBasket for why, and how it is labelled). Empty ⇒ the
 * screen shows the prose-only note instead. Pure; exported for its own test.
 */
export function stageEditOffers(payload: StageReviewPayload, basket: readonly StageEditPatch[] = []): EditOffer[] {
  if (payload.read.state !== 'ready' || payload.read.doc === null || payload.read.doc === undefined) return [];
  switch (payload.stage) {
    case 'statemachine':
      return lifecycleOffers(payload.read.doc!, basket);
    case 'process':
      return operationOffers(payload.read.doc!);
    case 'rules':
      return rulesOffers(payload.read.doc!);
    case 'rbac':
      return permissionOffers(payload.read.doc!);
    case 'audit':
      return auditOffers(payload.read.doc!);
    case 'decomposition':
      // No exact vocabulary, on purpose. See ProseOnlyNote.
      return [];
    default: {
      const exhaustive: never = payload;
      throw new Error(`unknown stage payload: ${String(exhaustive)}`);
    }
  }
}

// ── the sentences ────────────────────────────────────────────────────────────

/** The stage names a person reads in the re-derivation sentence (never the chain's words). */
const REDERIVE_WORDS: Readonly<Record<StageReviewKey, string>> = {
  statemachine: 'the lifecycles',
  process: 'what the app does',
  rules: 'the rules',
  rbac: 'the permissions',
  audit: 'the record-keeping',
  decomposition: 'the modules',
};

/** The truthful cost line: the change itself is free; what is already built on top is not. */
export function rederiveSentence(rederives: readonly StageReviewKey[]): string {
  if (rederives.length === 0) {
    return 'Nothing is derived again for this: the change is applied to what is already here, and the parts that come after are built on it when they run.';
  }
  const words = rederives.map((s) => REDERIVE_WORDS[s]);
  const list = words.length === 1 ? words[0] : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
  return `The change itself costs nothing — no thinking is redone to make it. What is already built on top of this is derived again: ${list}. That is paid work.`;
}

/** The sentence an order outcome earns — each one a fact the server reported. */
export function orderResultSentence(result: OrderStageEditResult): string {
  switch (result.outcome) {
    case 'refused':
      return 'That change was not made.';
    case 'no-change':
      return result.reason;
    case 'already-ordered':
      return result.resumed
        ? 'That exact change was already ordered — the machine is continuing with it.'
        : 'That exact change was already ordered; nothing new was recorded.';
    case 'applied':
      return result.resumed ? 'Ordered — the machine is applying it now.' : 'Ordered. It is applied the next time the machine runs.';
  }
}

/** The sentence a basket apply earns. */
export function basketResultSentence(result: ApplyStageBasketResult): string {
  if (result.outcome === 'refused') return 'Nothing was applied — one of these changes cannot be made. Fix or remove it and try again.';
  if (result.outcome === 'nothing-to-apply') return 'Nothing to apply — every change in the basket is already true of the model.';
  const noop = result.noopCount > 0 ? ` (${result.noopCount} changed nothing)` : '';
  return result.resumed
    ? `${result.appliedCount} change(s) ordered${noop} — the machine is applying them now.`
    : `${result.appliedCount} change(s) ordered${noop}. They are applied the next time the machine runs.`;
}

/** The MODULES review's honest answer: this one can only be asked. */
export function ProseOnlyNote({ stage }: { stage: StageReviewKey }) {
  const { subject } = STAGE_SUBJECTS[stage];
  return (
    <p className="sr__readonly" role="note">
      There is no exact change to make here. Everything else in the build has parts you can name and
      change — a state, an operation, a permission — but {subject} is a summary of how the whole app was
      cut up, and the pieces the app is actually built from are made in the same breath. Changing the
      summary afterwards would not change the app. So this one can only be asked, in your own words below.
    </p>
  );
}

// ── the door ─────────────────────────────────────────────────────────────────

/**
 * ── RESOLVE THE FIELDS, THEN RE-ASK FOR THEM ────────────────────────────────────────────────────
 * The fields are a FUNCTION of the answers so far (the state list belongs to the chosen machine, the
 * detail list to the chosen record), so a single pass would ask for the dependent field's choices with
 * the parent still unanswered and get an empty list. Each pass fills every select that has no valid
 * answer with its first choice and asks again; the walk is bounded by the deepest chain a vocabulary
 * has (three), with one extra pass to detect the fixed point.
 *
 * Pure, and shared: the render uses it to draw the form, and a SEEDED gesture uses it to build the
 * change it is about to dry-run — so a gesture's preview describes exactly the form on screen.
 */
export function resolveOfferValues(
  offer: EditOffer,
  values: Readonly<Record<string, string>>,
): { readonly fields: readonly EditField[]; readonly resolved: Record<string, string> } {
  let resolved: Record<string, string> = {};
  let fields = offer.fields(resolved);
  for (let pass = 0; pass < 4; pass += 1) {
    fields = offer.fields(resolved);
    const next: Record<string, string> = {};
    for (const f of fields) {
      const current = values[f.key] ?? resolved[f.key];
      if (f.kind === 'select') {
        const ok = current !== undefined && f.choices.some((c) => c.value === current);
        next[f.key] = ok ? current : (f.choices[0]?.value ?? '');
      } else if (current !== undefined) {
        next[f.key] = current;
      }
    }
    if (JSON.stringify(next) === JSON.stringify(resolved)) break;
    resolved = next;
  }
  return { fields, resolved };
}

/**
 * A gesture's compiled intent, handed to this door: the kind it picked, the values it named, and
 * whether the dry run can run at once. THE SEED CHANGES NOTHING ABOUT THE DOOR — it fills in the
 * same form a person would fill in by hand, and the same preview and the same buttons decide
 * everything after that. `token` is bumped per gesture so the same gesture twice re-seeds instead
 * of silently doing nothing. (The ER canvas's `EditComposerSeed`, in this door's register.)
 */
export interface StageEditSeed {
  readonly kind: StageEditPatch['kind'];
  readonly values: Readonly<Record<string, string>>;
  /** Only ever set for a COMPLETE gesture — one that already names everything the change needs. */
  readonly autoPreview?: boolean;
  /** The field key the gesture points at, focused once the seeded form has rendered. */
  readonly focus?: string;
  readonly token: number;
}

export interface StageEditDoorProps {
  readonly stage: StageReviewKey;
  readonly payload: StageReviewPayload;
  /** The later stages a landed change re-derives, from the server's disk read. */
  readonly rederives: readonly StageReviewKey[];
  readonly onPreview: (edit: StageEditPatch) => Promise<RunStageEditPreview>;
  readonly onOrder: (edit: StageEditPatch) => Promise<OrderStageEditResult>;
  readonly onPreviewBasket: (edits: StageEditPatch[]) => Promise<RunStageBasketPreview>;
  readonly onApplyBasket: (edits: StageEditPatch[]) => Promise<ApplyStageBasketResult>;
  /** A gesture from a picture on this screen, prefilling this door; null when opened by hand. */
  readonly seed?: StageEditSeed | null;
  /** The seed was applied to the form — the parent clears it, so it can never replay on a
   *  later remount of this door (a gesture is one instruction, not a standing one). */
  readonly onSeedConsumed?: () => void;
  /** THE BASKET STAYS THIS DOOR'S OWN. It is only REPORTED upward, so the pictures above can mark
   *  a state or a move that is in the basket as pending — never as something the machine derived. */
  readonly onBasketChange?: (basket: readonly StageEditPatch[]) => void;
}

export function StageEditDoor({
  stage,
  payload,
  rederives,
  onPreview,
  onOrder,
  onPreviewBasket,
  onApplyBasket,
  seed = null,
  onSeedConsumed,
  onBasketChange,
}: StageEditDoorProps) {
  const [basket, setBasket] = useState<StageEditPatch[]>([]);
  const offers = stageEditOffers(payload, basket);
  const [offerKind, setOfferKind] = useState<string>(offers[0]?.kind ?? '');
  const [values, setValues] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<RunStageEditPreview | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [basketPreview, setBasketPreview] = useState<RunStageBasketPreview | null>(null);
  const [basketNote, setBasketNote] = useState<string | null>(null);
  /** The control a gesture pointed at — focused one render later, once it exists. */
  const [focusId, setFocusId] = useState<string | null>(null);

  /** THE STALE-RESPONSE GUARD (the data-model composer's, verbatim in mechanism). Two rapid
   *  gestures race two dry runs; the last-RESOLVING response must never win the pane while
   *  the buttons commit the change built from the CURRENT form. `previewSeq` numbers every
   *  request; `previewWant` names the one whose response may still land (0 = none). */
  const previewSeq = useRef(0);
  const previewWant = useRef(0);
  function invalidatePreview(): void {
    previewWant.current = 0;
  }

  // A basket mutation makes a shown preview stale (it described a change landing after a basket that no
  // longer exists in that form) — drop it rather than let it mislead. The data-model screen's law.
  useEffect(() => {
    setBasketPreview(null);
    onBasketChange?.(basket);
    // The reporting callback is deliberately not a dependency: the parent re-creating it must not
    // re-run this, and the basket is the only thing that can honestly change what it reports.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basket]);

  const ids = `se-${stage}`;

  /* A GESTURE FROM A PICTURE fills this form in and nothing else. A COMPLETE gesture also runs the
     dry run at once, so the server's own sentence — what would change, and every caution — is on
     screen before any button that commits exists. An INCOMPLETE gesture puts the cursor in the
     input it still needs and waits for the person, exactly like the hand path. */
  const seedToken = seed?.token;
  useEffect(() => {
    if (seed === null || seed === undefined || seedToken === undefined) return;
    setOfferKind(seed.kind);
    setValues({ ...seed.values });
    setNote(null);
    invalidatePreview(); // a dry run an earlier gesture started must not land under this one
    setPreview(null);
    const target = offers.find((o) => o.kind === seed.kind);
    // A seed is CONSUMED the moment it has filled the form: the parent nulls it, so a later
    // remount of this door (the next halt at this stage, a payload refetch) replays nothing —
    // least of all a destructive dry run nobody just asked for.
    onSeedConsumed?.();
    if (seed.autoPreview === true && target !== undefined) {
      const complete = target.build(resolveOfferValues(target, seed.values).resolved);
      if (complete !== null) {
        void runPreviewOf(complete);
        return;
      }
    }
    if (seed.focus !== undefined) setFocusId(`${ids}-${seed.focus}`);
    // Re-seeding is keyed on the gesture's own token: the same gesture twice must re-fill.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedToken]);

  // Focus lands one render later — the control the gesture points at does not exist until the
  // seeded kind has rendered its own inputs.
  useEffect(() => {
    if (focusId === null) return;
    document.getElementById(focusId)?.focus();
    setFocusId(null);
  }, [focusId]);

  if (offers.length === 0) return <ProseOnlyNote stage={stage} />;

  const offer = offers.find((o) => o.kind === offerKind) ?? offers[0];
  const { fields, resolved } = resolveOfferValues(offer, values);
  const edit = offer.build(resolved);

  function set(key: string, v: string): void {
    setValues((prior) => ({ ...prior, [key]: v }));
    invalidatePreview(); // an in-flight dry run now describes a change that is no longer composed
    setPreview(null);
    setNote(null);
  }

  async function runPreviewOf(candidate: StageEditPatch): Promise<void> {
    const req = ++previewSeq.current;
    previewWant.current = req;
    setBusy(true);
    setNote(null);
    try {
      const result = await onPreview(candidate);
      if (previewWant.current !== req) return; // superseded — it must never land under the wrong change
      setPreview(result);
    } catch (err) {
      if (previewWant.current !== req) return; // a superseded failure is not this change's failure
      setNote(runErrorMessage(err));
    } finally {
      // Only the LATEST-STARTED request may settle the busy flag; an older one yielding here
      // would re-enable the buttons while a newer dry run is still in flight.
      if (previewSeq.current === req) setBusy(false);
    }
  }

  async function runPreview(): Promise<void> {
    if (edit === null) return;
    await runPreviewOf(edit);
  }

  async function order(): Promise<void> {
    if (edit === null) return;
    invalidatePreview(); // an in-flight dry run must not repaint the pane over the order's outcome
    setBusy(true);
    setNote(null);
    try {
      const result = await onOrder(edit);
      setNote(orderResultSentence(result));
      setPreview(result.outcome === 'refused' ? { kind: 'refused', refusals: result.refusals } : null);
    } catch (err) {
      setNote(runErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function runBasketPreview(): Promise<void> {
    if (basket.length === 0) return;
    setBusy(true);
    setBasketNote(null);
    try {
      setBasketPreview(await onPreviewBasket(basket));
    } catch (err) {
      setBasketNote(runErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function applyBasket(): Promise<void> {
    if (basket.length === 0) return;
    setBusy(true);
    setBasketNote(null);
    try {
      const result = await onApplyBasket(basket);
      setBasketNote(basketResultSentence(result));
      if (result.outcome === 'applied') setBasket([]);
      else setBasketPreview({ items: result.items, applicable: false, rederives: [...rederives] });
    } catch (err) {
      setBasketNote(runErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="dm-edit" role="region" aria-label="Change this exactly">
      <h3 className="sr__section">Change this exactly</h3>
      <p className="dm-edit__consequence">
        Below the words door: an exact change. The machine does not think about this one — it is applied
        as written, so it cannot come back ignored.
      </p>

      <div className="dm-edit__row">
        <label className="dm-edit__label" htmlFor={`${ids}-op`}>
          What do you want to change?
        </label>
        <select
          id={`${ids}-op`}
          className="dm-edit__select"
          value={offer.kind}
          onChange={(e) => {
            setOfferKind(e.target.value);
            setValues({});
            invalidatePreview();
            setPreview(null);
            setNote(null);
          }}
        >
          {offers.map((o) => (
            <option key={o.kind} value={o.kind}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {fields.map((f) => (
        <div className="dm-edit__row" key={f.key}>
          <label className="dm-edit__label" htmlFor={`${ids}-${f.key}`}>
            {f.label}
          </label>
          {f.kind === 'select' ? (
            <select
              id={`${ids}-${f.key}`}
              className="dm-edit__select"
              value={resolved[f.key] ?? ''}
              onChange={(e) => set(f.key, e.target.value)}
            >
              {f.choices.length === 0 && <option value="">nothing to choose</option>}
              {f.choices.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              id={`${ids}-${f.key}`}
              className="dm-edit__input"
              type="text"
              placeholder={f.hint ?? ''}
              value={resolved[f.key] ?? ''}
              onChange={(e) => set(f.key, e.target.value)}
            />
          )}
        </div>
      ))}

      <div className="dm-edit__actions">
        <button className="btn btn--ghost" type="button" onClick={() => void runPreview()} disabled={busy || edit === null}>
          {busy ? 'Checking…' : 'What would this do?'}
        </button>
        <button
          className="btn btn--quiet"
          type="button"
          disabled={edit === null}
          onClick={() => {
            if (edit === null) return;
            setBasket((prior) => [...prior, edit]);
            invalidatePreview();
            setPreview(null);
            setNote('Added to the basket — nothing is committed until you apply the basket.');
          }}
        >
          Add to basket
        </button>
        {preview !== null && preview.kind === 'change' && (
          <button className="btn btn--primary" type="button" onClick={() => void order()} disabled={busy}>
            {busy ? 'Ordering…' : 'Make this change'}
          </button>
        )}
      </div>

      {preview !== null && (
        <div
          className={`dm-edit__preview${preview.kind === 'refused' ? ' dm-edit__preview--refused' : preview.kind === 'no-change' ? ' dm-edit__preview--nochange' : ''}`}
          role="status"
        >
          {preview.kind === 'refused' && (
            <>
              <p className="dm-edit__sentence">That change cannot be made:</p>
              <ul className="dm-edit__list">
                {preview.refusals.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </>
          )}
          {preview.kind === 'no-change' && <p className="dm-edit__sentence">{preview.reason}</p>}
          {preview.kind === 'change' && (
            <>
              <p className="dm-edit__sentence">This is what would change:</p>
              <ul className="dm-edit__list">
                {preview.changes.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
              {preview.cautions.length > 0 && (
                <ul className="dm-edit__list dm-edit__list--cautions">
                  {preview.cautions.map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
              )}
              {preview.alsoNamed.length > 0 && (
                <>
                  <p className="dm-edit__sentence">What else in this build names it today:</p>
                  <ul className="dm-edit__list dm-edit__list--cautions">
                    {preview.alsoNamed.map((c, i) => (
                      <li key={i}>{c}</li>
                    ))}
                  </ul>
                </>
              )}
              {preview.alreadyOrdered && (
                <p className="dm-edit__sentence">You have already ordered this exact change on this build.</p>
              )}
              <p className="dm-edit__consequence">{rederiveSentence(preview.rederives as StageReviewKey[])}</p>
            </>
          )}
        </div>
      )}

      {note !== null && (
        <p className="sr-door__note" role="status">
          {note}
        </p>
      )}

      {basket.length > 0 && (
        <div className="dm-basket" role="region" aria-label="Basket">
          <h3 className="dm__section">{basket.length} change(s) in the basket</h3>
          <p className="dm-basket__order">
            They are applied in this order, each one landing on the model as the ones before it left it.
          </p>
          <ul className="dm-basket__list">
            {basket.map((b, i) => (
              <li className="dm-basket__row" key={i}>
                <span className="dm-basket__what">{basketPreview?.items[i]?.summary ?? describeLocally(b)}</span>
                <span className="dm-basket__tools">
                  <button
                    className="btn btn--quiet"
                    type="button"
                    onClick={() => setBasket((prior) => prior.filter((_, j) => j !== i))}
                  >
                    Remove
                  </button>
                </span>
              </li>
            ))}
          </ul>
          <div className="dm-edit__actions">
            <button className="btn btn--ghost" type="button" onClick={() => void runBasketPreview()} disabled={busy}>
              {busy ? 'Checking…' : 'What would all of these do?'}
            </button>
            {basketPreview !== null && basketPreview.applicable && (
              <button className="btn btn--primary" type="button" onClick={() => void applyBasket()} disabled={busy}>
                {busy ? 'Applying…' : 'Make all of these changes'}
              </button>
            )}
          </div>
          {basketPreview !== null && (
            <div className="dm-basket__outcomes">
              {basketPreview.items.map((item, i) => (
                <div
                  className={`dm-basket__outcome${item.outcome.kind === 'refused' ? ' dm-basket__outcome--refused' : ''}`}
                  key={i}
                >
                  <span className="dm-basket__outcome-head">{item.summary}</span>
                  {item.outcome.kind === 'refused' && (
                    <ul className="dm-edit__list">
                      {item.outcome.refusals.map((r, j) => (
                        <li key={j}>{r}</li>
                      ))}
                    </ul>
                  )}
                  {item.outcome.kind === 'no-op' && <span className="dm-basket__noop">{item.outcome.reason}</span>}
                  {item.outcome.kind === 'change' && (
                    <>
                      <ul className="dm-edit__list">
                        {item.outcome.changes.map((c, j) => (
                          <li key={j}>{c}</li>
                        ))}
                      </ul>
                      {item.outcome.cautions.length > 0 && (
                        <ul className="dm-edit__list dm-edit__list--cautions">
                          {item.outcome.cautions.map((c, j) => (
                            <li key={j}>{c}</li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                </div>
              ))}
              {!basketPreview.applicable && (
                <p className="dm-edit__sentence">
                  Nothing is applied while one of these cannot be made — fix or remove that one.
                </p>
              )}
              <p className="dm-edit__consequence">{rederiveSentence(basketPreview.rederives as StageReviewKey[])}</p>
            </div>
          )}
          {basketNote !== null && (
            <p className="sr-door__note" role="status">
              {basketNote}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * A basket row's words BEFORE the server has been asked. The server's own `summary` is the authority (it
 * is the sentence the ledger records); this is the placeholder until a preview returns one, built from the
 * change itself so a person is never shown a blank row.
 */
function describeLocally(p: StageEditPatch): string {
  switch (p.kind) {
    case 'sm-state-add':
      return `Add the state '${p.state}' to ${p.entity}`;
    case 'sm-state-rename':
      return `Rename ${p.entity}'s state '${p.from}' to '${p.to}'`;
    case 'sm-state-remove':
      return `Remove the state '${p.state}' from ${p.entity}`;
    case 'sm-transition-add':
      return `Let ${p.entity} move from '${p.from}' to '${p.to}' on ${p.event}`;
    case 'sm-transition-remove':
      return `Stop ${p.entity} moving from '${p.from}' to '${p.to}' on ${p.event}`;
    case 'sm-terminal-set':
      return p.terminal ? `Make '${p.state}' a final state for ${p.entity}` : `Stop '${p.state}' being a final state for ${p.entity}`;
    case 'pr-operation-remove':
      return `Remove the operation '${p.operation}'`;
    case 'ru-precondition-remove':
      return `Remove one requirement from '${p.operation}'`;
    case 'ru-errorcase-remove':
      return `Remove one recorded error from '${p.operation}'`;
    case 'ru-invariant-remove':
      return 'Remove one always-true rule';
    case 'rb-grant-add':
      return `Let ${p.role} do '${p.permission}'`;
    case 'rb-grant-remove':
      return `Stop ${p.role} doing '${p.permission}'`;
    case 'rb-role-remove':
      return `Remove the role ${p.role}`;
    case 'au-entry-remove':
      return `Stop recording '${p.action}' on '${p.operation}'`;
    case 'au-detail-field-remove':
      return `Stop recording '${p.field}' in '${p.action}' on '${p.operation}'`;
  }
}
